import styled, { keyframes } from "styled-components";

const priceFadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const cardEntrance = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const ModernPlanCardContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 500px;
  width: 100%;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.09);
  box-shadow: 0px 1px 2px 0px rgba(10, 13, 18, 0.04);
  background: #fff;
  overflow: hidden;
  font-family: "Geist", sans-serif;
  animation: ${cardEntrance} 0.28s ease both;
  animation-delay: ${({ $index }) => $index * 0.07}s;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    border-color: rgba(10, 13, 18, 0.22);
    box-shadow: 0px 2px 8px 0px rgba(10, 13, 18, 0.08);
    transform: translateY(-2px);
  }

  &.header-silver {
    background: linear-gradient(
      63.8deg,
      rgb(222, 225, 229) 0%,
      rgb(242, 243, 245) 100%
    );
  }

  &.header-green {
    background: linear-gradient(
      63.8deg,
      rgb(224, 214, 238) 0%,
      rgb(244, 240, 250) 100%
    );
  }

  &.header-blue {
    background: linear-gradient(
      63.8deg,
      rgb(206, 222, 237) 0%,
      rgb(239, 244, 249) 100%
    );
  }

  &.header-warm {
    background: linear-gradient(
      63.8deg,
      rgb(227, 232, 210) 0%,
      rgb(243, 245, 235) 100%
    );
  }

  .plan-header {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 24px 24px 20px;

    .plan-name {
      font-size: 20px;
      font-weight: 500;
      line-height: 28px;
      letter-spacing: -0.2px;
      color: #181d27;
      margin: 0;
    }

    .pricing-section {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .price-display {
        display: flex;
        align-items: center;
        gap: 4px;
        flex-wrap: wrap;
      }

      .price-amount {
        font-size: 30px;
        font-weight: 500;
        line-height: 34px;
        letter-spacing: -0.6px;
        color: #181d27;
        animation: ${priceFadeIn} 0.18s ease;

        &.strike-price {
          font-size: 20px;
          font-weight: 400;
          color: #9aa4b0;
          text-decoration: line-through;
          letter-spacing: -0.4px;
          line-height: 34px;
          animation: none;
        }

        &.line-through {
          text-decoration: line-through;
          opacity: 0.7;
        }

        &.discounted {
          color: #fbbf24;
          margin-left: 8px;
        }

        &.custom {
          font-size: 30px;
          font-weight: 500;
        }

        .dot-price {
          font-size: 30px;
          font-weight: 500;
        }
      }

      .price-period {
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        color: #717680;
        margin-left: 4px;
        align-self: flex-end;
        padding-bottom: 2px;
      }

      .custom-description {
        font-size: 14px;
        color: #414651;
        margin-top: 4px;
        line-height: 20px;
      }
    }
  }

  .button-section {
    padding: 0 24px 24px;

    .plan-button {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      padding: 12px 18px;
      border-radius: 8px;
      cursor: pointer;
      transition: background 0.2s ease, border-color 0.2s ease,
        opacity 0.2s ease;

      .button-loader {
        width: 16px !important;
        height: 16px !important;

        .loader {
          color: #fff;

          .spinner-border {
            width: 16px;
            height: 16px;
            border-width: 2px;
          }
        }
      }

      .button-text {
        font-size: 14px;
        font-weight: 500;
        line-height: 20px;
        white-space: nowrap;
      }

      &.action-plan {
        background: #181d27;
        border: 1px solid #181d27;

        .button-text {
          color: #fff;
        }

        &:hover:not(:disabled) {
          background: #2d3748;
          border-color: #2d3748;
        }

        &:disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }
      }

      &.current-plan {
        background: rgba(255, 255, 255, 0.6);
        border: 1px solid rgba(0, 0, 0, 0.12);
        cursor: default;
        backdrop-filter: blur(4px);

        .button-text {
          color: rgba(10, 13, 18, 0.6);
        }

        &:disabled {
          opacity: 1;
        }
      }

      &.downgrade-scheduled {
        background: #f2f4f7;
        border: 1px solid #d5d7da;
        cursor: default;

        .button-text {
          color: #717680;
        }

        &:disabled {
          opacity: 1;
        }
      }
    }
  }

  .card-divider {
    height: 1px;
    background: rgba(0, 0, 0, 0.06);
    flex-shrink: 0;
  }

  .benefits-section {
    flex: 1;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 14px;

    .benefits-header {
      font-size: 14px;
      font-weight: 500;
      letter-spacing: -0.1px;
      color: #181d27;
      line-height: 20px;
      margin: 0;
    }

    .benefits-list {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .benefit-item {
      display: flex;
      align-items: flex-start;
      gap: 10px;

      .check-icon {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        padding-top: 2px;
        color: #181d27;
      }

      .benefit-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 4px;

        .benefit-tag {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          color: black;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 2px;
          width: fit-content;
        }

        .benefit-text {
          font-size: 14px;
          font-weight: 400;
          letter-spacing: -0.1px;
          color: #414651;
          line-height: 20px;
        }

        .benefit-asterisk {
          font-size: 10px;
          vertical-align: super;
          margin-left: 1px;
        }
      }
    }
  }

  @media (max-width: 768px) {
    min-height: 450px;

    .plan-header {
      padding: 20px 20px 16px;
    }

    .button-section {
      padding: 0 20px 20px;
    }

    .benefits-section {
      padding: 20px;
    }
  }

  @media (max-width: 480px) {
    min-height: 400px;

    .plan-header {
      padding: 16px 16px 12px;
    }

    .button-section {
      padding: 0 16px 16px;

      .plan-button {
        padding: 10px 14px;

        .button-text {
          font-size: 14px;
        }
      }
    }

    .benefits-section {
      padding: 16px;

      .benefit-item .check-icon svg {
        width: 12px;
        height: 12px;
      }

      .benefit-item .benefit-content .benefit-text {
        font-size: 13px;
      }
    }
  }
`;
